import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { CalendarCheck } from "lucide-react";

const PLATFORMS = ["Teams", "Zoom", "Google Meet", "Webex", "Other"];

export default function MeetingBookingPanel({ leadId, region, onBooked }) {
  const [reps, setReps] = useState([]);
  const [rep, setRep] = useState("");
  const [slot, setSlot] = useState("");
  const [useOwnLink, setUseOwnLink] = useState(false);
  const [platform, setPlatform] = useState("Zoom");
  const [url, setUrl] = useState("");
  const [transcription, setTranscription] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/users").then((r) => setReps(r.data.filter((u) => u.status !== "departed"))); }, []);

  const book = async () => {
    if (!rep || !slot) { toast.error("Pick a rep and a slot"); return; }
    if (useOwnLink && !url.trim()) { toast.error("Enter the lead's meeting URL"); return; }
    setBusy(true);
    const payload = { rep_id: rep, slot };
    if (useOwnLink) payload.meeting_link = { platform, url };
    else payload.transcription_enabled = transcription;
    try {
      const r = await api.post(`/leads/${leadId}/book-meeting`, payload);
      toast.success(r.data?.calendar_sync === "outlook_one_way"
        ? "Booked — real Teams meeting created in the host's Outlook calendar (one-way sync)"
        : "Meeting booked in CRM — coverage verified (host's calendar not connected)");
      onBooked?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Booking failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-[#DFDAD0] bg-white p-5" data-testid="meeting-booking-panel">
      <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-1">Voice Agent · Meeting Booking</div>
      <div className="font-mono text-[10px] text-[#1C2530]/45 mb-3">
        Region: <span className="text-[#1C2530]/70">{region}</span> · reps need active coverage at the slot; Sales Leads / Admins can host without it · no CRM double-booking · <span className="text-[#3E8F99]">hosts who reconnected Outlook get a real Outlook free/busy check + a genuine Teams event (one-way: CRM → Outlook)</span>; otherwise <span className="text-[#E2711D]">CRM-only booking, calendar not synced</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <select value={rep} onChange={(e) => setRep(e.target.value)} data-testid="booking-rep"
          className="border border-[#DFDAD0] px-2 py-1.5 text-sm bg-white outline-none focus:border-[#1C2530]">
          <option value="">Select host…</option>
          {reps.map((r) => <option key={r.id} value={r.id}>{r.name} · {r.role}{r.region ? ` (${r.region})` : ""}</option>)}
        </select>
        <input type="datetime-local" value={slot} onChange={(e) => setSlot(e.target.value)} data-testid="booking-slot"
          className="border border-[#DFDAD0] px-2 py-1.5 text-sm outline-none focus:border-[#1C2530]" />
        <button onClick={book} disabled={busy} data-testid="booking-submit"
          className="flex items-center justify-center gap-2 bg-[#1C2530] text-[#F5F3EE] px-3 py-1.5 text-xs font-mono uppercase tracking-wider hover:bg-[#2a3644] disabled:opacity-40">
          <CalendarCheck className="w-3.5 h-3.5" /> {busy ? "Booking…" : "Book Slot"}
        </button>
      </div>
      <div className="mt-3 pt-3 border-t border-[#DFDAD0] space-y-2">
        <label className="flex items-center gap-2 text-xs text-[#1C2530]/80 cursor-pointer">
          <input type="checkbox" checked={useOwnLink} onChange={(e) => setUseOwnLink(e.target.checked)} data-testid="booking-use-own-link" />
          Lead shared their own meeting link (use instead of default Teams)
        </label>
        {useOwnLink ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <select value={platform} onChange={(e) => setPlatform(e.target.value)} data-testid="booking-platform"
              className="border border-[#DFDAD0] px-2 py-1.5 text-sm bg-white outline-none focus:border-[#1C2530]">
              {PLATFORMS.map((p) => <option key={p}>{p}</option>)}
            </select>
            <input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="booking-link-url" placeholder="https://…"
              className="md:col-span-2 border border-[#DFDAD0] px-2 py-1.5 text-sm outline-none focus:border-[#1C2530]" />
          </div>
        ) : (
          <label className="flex items-center gap-2 text-xs text-[#1C2530]/70 cursor-pointer">
            <input type="checkbox" checked={transcription} onChange={(e) => setTranscription(e.target.checked)} data-testid="booking-transcription" />
            Teams transcription enabled (MOM can be drafted from transcript)
          </label>
        )}
      </div>
    </div>
  );
}
